// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * World↔screen transform.
 *
 * The renderer draws in board millimetres and lets the canvas transform do the
 * work, so a 0.25 mm track is asked for as 0.25 and scales correctly at every
 * zoom. Because we own this, the diff overlay is exactly aligned by
 * construction — §6.7's worry about reaching into a third-party renderer's
 * camera does not arise.
 */

import type { BBox, MaybeBBox } from '../model/bbox.ts';

export interface Viewport {
  width: number;
  height: number;
}

export class Camera {
  /** Screen pixels per millimetre. */
  scale = 1;
  /** World coordinate at the top-left of the viewport. */
  x = 0;
  y = 0;

  clone(): Camera {
    const c = new Camera();
    c.scale = this.scale;
    c.x = this.x;
    c.y = this.y;
    return c;
  }

  copyFrom(other: Camera): void {
    this.scale = other.scale;
    this.x = other.x;
    this.y = other.y;
  }

  toScreenX(worldX: number): number {
    return (worldX - this.x) * this.scale;
  }
  toScreenY(worldY: number): number {
    return (worldY - this.y) * this.scale;
  }
  toWorldX(screenX: number): number {
    return screenX / this.scale + this.x;
  }
  toWorldY(screenY: number): number {
    return screenY / this.scale + this.y;
  }

  /** The world rectangle currently visible, for culling. */
  visibleBox(view: Viewport): BBox {
    return {
      minX: this.x,
      minY: this.y,
      maxX: this.x + view.width / this.scale,
      maxY: this.y + view.height / this.scale,
    };
  }

  /**
   * Fit `box` into the viewport with a pixel margin.
   *
   * Returns false when there was nothing to fit *or the viewport has no size
   * yet* — which is the normal state during first layout. The caller must not
   * record a failed fit as done, or the view stays at its default zoom forever.
   */
  fit(box: MaybeBBox, view: Viewport, margin = 24): boolean {
    if (box === null || view.width <= 0 || view.height <= 0) return false;
    const w = Math.max(box.maxX - box.minX, 1e-3);
    const h = Math.max(box.maxY - box.minY, 1e-3);
    const usableW = Math.max(view.width - margin * 2, 1);
    const usableH = Math.max(view.height - margin * 2, 1);
    this.scale = Math.min(usableW / w, usableH / h);
    this.centerOn((box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2, view);
    return true;
  }

  centerOn(worldX: number, worldY: number, view: Viewport): void {
    this.x = worldX - view.width / 2 / this.scale;
    this.y = worldY - view.height / 2 / this.scale;
  }

  /** Zoom about a fixed screen point, so the content under the cursor stays put. */
  zoomAt(screenX: number, screenY: number, factor: number, limits = { min: 0.02, max: 4000 }): void {
    const worldX = this.toWorldX(screenX);
    const worldY = this.toWorldY(screenY);
    this.scale = Math.min(limits.max, Math.max(limits.min, this.scale * factor));
    this.x = worldX - screenX / this.scale;
    this.y = worldY - screenY / this.scale;
  }

  panByScreen(dx: number, dy: number): void {
    this.x -= dx / this.scale;
    this.y -= dy / this.scale;
  }

  /**
   * Apply to a 2D context so subsequent drawing is in world millimetres.
   * `dpr` folds in the device pixel ratio.
   */
  applyTo(ctx: CanvasRenderingContext2D, dpr: number): void {
    ctx.setTransform(this.scale * dpr, 0, 0, this.scale * dpr, -this.x * this.scale * dpr, -this.y * this.scale * dpr);
  }

  /** Width in world units of something that should be `px` screen pixels wide. */
  pixelSize(px = 1): number {
    return px / this.scale;
  }
}
